import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  RefreshCw, Wallet, TrendingUp, Users, ReceiptText, AlertTriangle,
  FileBarChart, ScrollText, Bell, ArrowRight,
} from "lucide-react";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

const METHOD_COLORS = { cash: "#16a34a", upi: "#0ea5e9", card: "#7c3aed", bank: "#f59e0b", insurance: "#ef4444", credit: "#64748b" };
const METHOD_ORDER = ["cash", "upi", "card", "bank", "insurance", "credit"];

export default function BillingDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [outstanding, setOutstanding] = useState({ rows: [], summary: {} });
  const [monthly, setMonthly] = useState({ rows: [] });
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, o, m, a] = await Promise.all([
        api.get("/billing/dashboard"),
        api.get("/billing/reports/outstanding_bills"),
        api.get(`/billing/reports/monthly_revenue?year=${new Date().getFullYear()}`),
        api.get("/billing/notifications"),
      ]);
      setDashboard(d.data);
      setOutstanding(o.data);
      setMonthly(m.data);
      setAlerts(a.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !dashboard) return <div className="text-sm text-muted-foreground p-6">Loading dashboard…</div>;

  const methodEntries = METHOD_ORDER
    .map((m) => ({ name: m.toUpperCase(), value: Number(dashboard.by_method_this_month?.[m] || 0), key: m }))
    .filter((x) => x.value > 0);
  const chartData = (monthly.rows || []).map((r) => ({ month: r.month.slice(5), revenue: r.revenue, refunds: r.refunds }));

  return (
    <div className="space-y-6" data-testid="billing-dashboard">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Billing</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Live collection, outstanding &amp; revenue signals.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={load} data-testid="btn-dash-refresh">
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
          <Link to="/billing/reports"><Button size="sm" variant="outline" className="rounded-full"><FileBarChart size={13} className="mr-1.5" /> Reports</Button></Link>
          <Link to="/billing/audit"><Button size="sm" variant="outline" className="rounded-full"><ScrollText size={13} className="mr-1.5" /> Audit</Button></Link>
          <Link to="/billing/config"><Button size="sm" variant="outline" className="rounded-full">Configuration <ArrowRight size={13} className="ml-1.5" /></Button></Link>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="card-clinical p-4 border-amber-500/60">
          <div className="flex items-center gap-2 mb-2">
            <Bell size={14} className="text-amber-500" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Alerts ({alerts.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {alerts.slice(0, 6).map((a) => (
              <Link key={`${a.patient_id}-${a.kind}`} to={`/patients/${a.patient_id}?tab=billing`}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 hover:bg-muted/30 ${a.severity === "critical" ? "border-destructive/60 bg-destructive/5" : "border-amber-500/40 bg-amber-500/5"}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle size={14} className={a.severity === "critical" ? "text-destructive" : "text-amber-500"} />
                  <div className="min-w-0">
                    <div className="text-sm truncate">{a.patient_name} <span className="text-muted-foreground text-[11px]">({a.hospital_id || "—"} · Bed {a.bed || "—"})</span></div>
                    <div className="text-[12px] text-muted-foreground truncate">{a.message}</div>
                  </div>
                </div>
                <div className="text-sm tabular font-medium ml-3">{inr(a.balance)}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Today's Collection" value={inr(dashboard.today_collection)} icon={Wallet} tone="ok" />
        <Kpi label="Month Revenue" value={inr(dashboard.month_revenue)} icon={TrendingUp} tone="ok" />
        <Kpi label="Outstanding Total" value={inr(dashboard.outstanding_total)} icon={ReceiptText} tone={dashboard.outstanding_total > 0 ? "warn" : "muted"} />
        <Kpi label="Open Bills / Admitted" value={`${dashboard.open_bills} / ${dashboard.admitted_patients}`} icon={Users} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-clinical p-4 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <BarChart size={14} className="text-primary" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Monthly revenue ({new Date().getFullYear()})</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <RTooltip formatter={(v) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Bar dataKey="revenue" fill="#0ea5e9" name="Revenue" radius={[4, 4, 0, 0]} />
              <Bar dataKey="refunds" fill="#ef4444" name="Refunds" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-clinical p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart size={14} className="text-primary" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">This month by method</span>
          </div>
          {methodEntries.length === 0 ? (
            <div className="text-sm text-muted-foreground">No payments this month.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={methodEntries} dataKey="value" nameKey="name" outerRadius={70} innerRadius={38}>
                  {methodEntries.map((e) => <Cell key={e.key} fill={METHOD_COLORS[e.key]} />)}
                </Pie>
                <RTooltip formatter={(v) => inr(v)} />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Outstanding table (top 10) */}
      <div className="card-clinical p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ReceiptText size={14} className="text-primary" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Outstanding bills</span>
            <Badge variant="outline" className="rounded-full text-[10px]">{outstanding.summary?.count || 0}</Badge>
          </div>
          <Link to="/billing/reports" className="text-xs text-primary hover:underline">See full report →</Link>
        </div>
        {(outstanding.rows || []).length === 0 ? (
          <div className="text-sm text-muted-foreground">Everyone's paid up 🎉</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Patient</th>
                  <th className="text-left px-3 py-2 font-medium">Bed</th>
                  <th className="text-left px-3 py-2 font-medium">Consultant</th>
                  <th className="text-right px-3 py-2 font-medium">Days</th>
                  <th className="text-right px-3 py-2 font-medium">Paid</th>
                  <th className="text-right px-3 py-2 font-medium">Balance</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(outstanding.rows || []).slice(0, 10).map((r) => (
                  <tr key={r.patient_id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-[11px] text-muted-foreground">{r.hospital_id}</div>
                    </td>
                    <td className="px-3 py-2">{r.bed}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.consultant}</td>
                    <td className="px-3 py-2 text-right tabular">{r.days_admitted ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular">{inr(r.paid)}</td>
                    <td className="px-3 py-2 text-right tabular font-medium text-amber-600 dark:text-amber-400">{inr(r.balance)}</td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/patients/${r.patient_id}?tab=billing`} className="text-primary text-xs hover:underline">Open →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const cls = tone === "warn" ? "text-amber-600 dark:text-amber-400"
    : tone === "ok" ? "text-emerald-600 dark:text-emerald-400"
    : tone === "muted" ? "text-muted-foreground" : "";
  return (
    <div className="card-clinical p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
        {Icon && <Icon size={14} className="text-primary/60" />}
      </div>
      <div className={`font-display font-semibold text-2xl mt-2 tabular ${cls}`}>{value}</div>
    </div>
  );
}
